import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react"

import type { ButtonBaseProps } from "@mui/material"
import { Box, ButtonBase, Stack, Typography } from "@mui/material"
import { alpha } from "@mui/material/styles"
import type { EmblaCarouselType } from "embla-carousel"
import Autoplay from "embla-carousel-autoplay"
import useEmblaCarousel from "embla-carousel-react"
import { truncate } from "lodash"

import { gsap } from "@ggs/ui/local/gsap"
import type { FC, FCWC } from ".."
import { Carousel, ResponsiveImage, SvgIcon, type ResponsiveImageType } from ".."

type VisualMediaContentType = {
  images: ResponsiveImageType[]
}

const VisualMediaContent: FC<VisualMediaContentType> = ({ images }) => {
  const [emblaRef, emblaApi] = useEmblaCarousel(
    {
      duration: 75,
      loop: false,
      align: "center",
      containScroll: "trimSnaps",
      dragFree: false,
      dragThreshold: 20,
    },
    [
      Autoplay({
        delay: 8000,
        stopOnMouseEnter: true,
      }),
    ]
  )
  const cursorContainer = useRef<HTMLDivElement>(null)
  const cursorArrow = useRef<HTMLButtonElement>(null)
  const [cursorActive, setCursorActive] = useState<"prev" | "next" | null>(null)
  const [cursorPos, setCursorPos] = useState<{ x: number; y: number }>({ x: -100, y: -100 })
  const scrollTrackRef = useRef<HTMLDivElement>(null)
  const scrollThumbRef = useRef<HTMLDivElement>(null)
  const [thumbWidth, setThumbWidth] = useState(20)
  const [trackWidth, setTrackWidth] = useState(1600)
  const [scrollSnaps, setScrollSnaps] = useState<number[]>([])
  const [canScrollPrev, setCanScrollPrev] = useState(false)
  const [canScrollNext, setCanScrollNext] = useState(false)
  const isVisible = Array.isArray(images) && images?.length > 0
  const trimLabel = useCallback(
    (label: string) => truncate(label, { length: 50, omission: "..." }),
    []
  )

  const onInit = useCallback(
    (emblaApi: EmblaCarouselType) => {
      if (!emblaApi) return
      setCanScrollPrev(emblaApi.canScrollPrev())
      setCanScrollNext(emblaApi.canScrollNext())

      if (scrollTrackRef.current) {
        const { clientWidth: trackSize } = scrollTrackRef.current
        const emblaClientWidth = emblaApi.containerNode().clientWidth
        const emblaScrollWidth = emblaApi.containerNode().scrollWidth
        setThumbWidth(Math.max((emblaClientWidth / emblaScrollWidth) * trackSize, 20))
        setTrackWidth(trackSize)
      }
    },
    [scrollTrackRef]
  )

  const onScroll = useCallback(
    (emblaApi: EmblaCarouselType) => {
      if (!emblaApi) return

      const snaps = emblaApi.scrollSnapList()
      setScrollSnaps(snaps)
      if (!scrollTrackRef.current || !scrollThumbRef.current) return

      const progress = emblaApi.scrollProgress()
      const { clientWidth: trackWidth } = scrollTrackRef.current
      const scrollX = progress * (trackWidth - thumbWidth)
      const thumb = scrollThumbRef.current
      thumb.style.left = `${scrollX}px`
    },
    [thumbWidth]
  )

  const onSelect = useCallback((emblaApi: EmblaCarouselType) => {
    if (!emblaApi) return

    setCanScrollPrev(emblaApi.canScrollPrev())
    setCanScrollNext(emblaApi.canScrollNext())
  }, [])

  const scrollPrev = useCallback(() => {
    if (emblaApi) {
      emblaApi.scrollPrev()
      if (!emblaApi.canScrollPrev()) {
        setCursorActive(null)
      }
    }
  }, [emblaApi])

  const scrollNext = useCallback(() => {
    if (emblaApi) {
      emblaApi.scrollNext()
      if (!emblaApi.canScrollNext()) {
        setCursorActive(null)
      }
    }
  }, [emblaApi])

  const scrollTo = useCallback(
    (index: number) => {
      if (emblaApi) emblaApi.scrollTo(index)
    },
    [emblaApi]
  )

  const handleTrackClick = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault()
      e.stopPropagation()

      const { current: trackCurrent } = scrollTrackRef

      if (!emblaApi || !trackCurrent) return

      const { clientX } = e
      const target = e.target as HTMLDivElement
      const rect = target.getBoundingClientRect()
      const trackLeft = rect.left
      const clickRatio = (clientX - trackLeft) / trackCurrent.clientWidth

      const closestSlide = scrollSnaps.reduce((prev, curr) => {
        return Math.abs(curr - clickRatio) < Math.abs(prev - clickRatio) ? curr : prev
      }, 0)
      const closestIndex = scrollSnaps.indexOf(closestSlide)
      emblaApi?.scrollTo(closestIndex)
    },
    [scrollSnaps, scrollTrackRef]
  )

  useEffect(() => {
    if (!emblaApi) return

    onInit(emblaApi)
    emblaApi.on("reInit", onInit)
    emblaApi.on("resize", onInit)
    emblaApi.on("select", onSelect)
    emblaApi.on("scroll", onScroll)
  }, [emblaApi, onInit, onScroll])

  useLayoutEffect(() => {
    if (!cursorArrow.current || !cursorContainer.current || cursorActive === null) return

    const ctx = gsap.context(() => {
      const render = () => {
        gsap.to(cursorArrow.current, {
          duration: 0.3,
          x: cursorPos.x,
          y: cursorPos.y,
          ease: "power2",
        })
      }
      requestAnimationFrame(render)
    }, cursorContainer.current)

    return () => ctx.revert()
  }, [cursorPos, cursorActive, cursorContainer, cursorArrow])

  // manually manage autoplay when cursor is active
  useEffect(() => {
    if (!emblaApi) return

    // get autoplay plugin
    const autoplay = emblaApi.plugins().autoplay
    if (!autoplay) return

    // if cursorActive is not null, autoplay is not active
    cursorActive !== null ? autoplay.stop() : autoplay.play()
  }, [emblaApi, cursorActive])

  if (isVisible) {
    return (
      <Box mb={{ xs: 20, md: 45 }} ref={cursorContainer} position="relative">
        <ButtonBase
          ref={cursorArrow}
          sx={{
            display: { xs: 'none', md: 'block' },
            color: "white",
            position: "fixed",
            opacity: cursorActive === null ? 0 : 1,
            top: 0,
            left: 0,
            zIndex: 100,
            pointerEvents: "none",
            cursor: "none",
          }}
          aria-label="Carousel button"
        >
          <Stack
            alignItems="center"
            justifyContent="center"
            position="relative"
            width={70}
            height={70}
            bgcolor={(theme) => theme.palette["neutral.100"].main}
            boxShadow={(theme) => `2px 2px 8px ${alpha(theme.palette["neutral.900"].main, 0.08)}`}
            borderRadius={9999}
            sx={{ transform: "translate(-50%, -50%)" }}
          >
            <SvgIcon
              variant="arrow"
              sx={{
                fill: (theme) => theme.palette["neutral.700"].main,
                ...(cursorActive === "next" && { transform: "rotate(180deg)" }),
              }}
            />
          </Stack>
        </ButtonBase>
        <Carousel.Root carouselRef={emblaRef} maxWidth="100vw" height={{ xs: 320, md: 600 }}>
          {images?.map((image, i) => (
            <VisualMediaSlide
              key={i}
              image={image}
              location={trimLabel(image?.variants?.[0]?.title)}
              onScroll={() => scrollTo(i)}
            />
          ))}
        </Carousel.Root>
        <Box display={{ xs: 'none', md: 'block' }}>
          {canScrollPrev && (
            <ButtonBase
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                bottom: 0,
                width: !canScrollNext ? "75%" : "50%",
                cursor: "none",
              }}
              onClick={scrollPrev}
              onMouseEnter={() => setCursorActive("prev")}
              onMouseMove={(e) => setCursorPos({ x: e.clientX, y: e.clientY })}
              onMouseLeave={() => setCursorActive(null)}
              aria-label="scroll previous"
              tabIndex={-1}
            />
          )}
          {canScrollNext && (
            <ButtonBase
              sx={{
                position: "absolute",
                top: 0,
                right: 0,
                bottom: 0,
                width: !canScrollPrev ? "75%" : "50%",
                cursor: "none",
              }}
              onClick={scrollNext}
              onMouseEnter={() => setCursorActive("next")}
              onMouseMove={(e) => setCursorPos({ x: e.clientX, y: e.clientY })}
              onMouseLeave={() => setCursorActive(null)}
              aria-label="scroll next"
              tabIndex={-1}
            />
          )}
        </Box>

        {thumbWidth !== trackWidth && (
          <Box height="2px" overflow="hidden" mt={{ xs: 5, md: 10 }} mx={5} position="relative">
            <Box
              ref={scrollTrackRef}
              onClick={(e) => handleTrackClick(e)}
              position="absolute"
              top={0}
              left={0}
              right={0}
              bottom={0}
              bgcolor={(theme) => theme.palette["neutral.200"].main}
              sx={{ "&:hover": { cursor: "pointer" } }}
            />
            <Box
              ref={scrollThumbRef}
              position="absolute"
              width={thumbWidth}
              top={0}
              left={0}
              bottom={0}
              bgcolor={(theme) => theme.palette["primary.800"].main}
            />
          </Box>
        )}
      </Box>
    )
  }

  return null
}

export default VisualMediaContent

type VisualMediaProps = {
  image: ResponsiveImageType
  location: string | null
  onScroll?: () => void
}

const VisualMediaSlide: FC<VisualMediaProps> = ({ image, location, onScroll }) => {
  return (
    <Carousel.Slide
      mx={2}
      borderRadius={1.5}
      position="relative"
      overflow="hidden"
      maxWidth="min(820px, 95%)"
      color={(theme) => theme.palette["neutral.100"].main}
      tabIndex={0}
      onFocus={onScroll}
      sx={{
        outline: 0,
      }}
    >
      <VisualMediaImage image={image} />
      <VisualMediaLocation location={location as string} />
    </Carousel.Slide>
  )
}

const VisualMediaImage: FC<{ image: ResponsiveImageType }> = ({ image }) => {
  const variants = []

  const lgImage = image.variants.find((variant) => variant.style === "3xl")
  const defaultImage = image.variants.find((variant) => variant.style === "default")

  if (lgImage) {
    variants.push({ ...lgImage, style: "default" })
  } else {
    variants.push(defaultImage!)
  }

  return (
    <ResponsiveImage
      image={{
        ...image,
        variants,
      }}
      position="absolute"
      width="100%"
      height="100%"
      zIndex={-1}
      sx={{
        objectFit: "cover",
        pointerEvents: "none",
      }}
    />
  )
}

const VisualMediaLocation: FC<{ location?: string }> = ({ location }) => {
  return (
    <>
      {location && (
        <Stack
          bgcolor={(theme) => alpha(theme.palette["neutral.900"].main, 0.05)}
          borderRadius={9999}
          direction="row"
          columnGap={2}
          alignItems="center"
          position="absolute"
          right={20}
          bottom={15}
          px={3}
          py={2}
          sx={{ backdropFilter: "blur(4px)" }}
        >
          <SvgIcon variant="location" size={{ xs: 8, md: 12 }} />
          <Typography variant="link.xs">{location}</Typography>
        </Stack>
      )}
    </>
  )
}

const CarouselBtn: FCWC<ButtonBaseProps> = ({ children, ...props }) => {
  return (
    <ButtonBase disableRipple {...props}>
      {children}
    </ButtonBase>
  )
}
